import { Component, Input, NgZone, OnDestroy, OnInit } from "@angular/core";
import {
  AnalyticsService,
  AuthorizationService,
  ExportService,
  LoggingService,
  ToastrService,
  UserLineChartDataPoint,
  UserPermissions,
  UserSessionsData,
} from "app/shared";
import { ChartData, ChartOptions, ChartType } from "chart.js";
//TODO: this StringFormatterService may be available to import from app/shared
import { StringFormatterService } from "app/shared/service/string-formatter.service";
import { BaseChartLogic } from "app/analytics/BaseChartLogic";
import { UserSessionsConfig } from "app/shared/model/charts/user-analytic-configurations.model";
import { ColorSchemes } from "app/analytics/plugins/colorschemes/colorschemes";

@Component({
  selector: "users-sessions-chart",
  templateUrl: "../../common-chart-template.component.html",
  standalone: false,
})
export class UsersSessionsChartComponent
  extends BaseChartLogic<UserSessionsConfig, UserSessionsData>
  implements OnInit, OnDestroy
{
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() showTitle: boolean = true;

  public userSessionData: ChartData<"line"> = {
    labels: ["January", "February", "March", "April", "May", "June", "July"],
    datasets: [
      {
        data: [22, 59, 80, 81, 105, 55, 117],
        label: "User Sessions",
        borderColor: "#3cba9f",
      },
    ],
  };
  public userSessionChartOptions: ChartOptions = {
    responsive: true,
  };
  public userSessionChartType: ChartType = "line";

  //TODO: what is StringFormatterService, why is it not imported?
  constructor(
    _analyticsService: AnalyticsService,
    _authService: AuthorizationService,
    _toastrService: ToastrService,
    _exportService: ExportService,
    _loggingService: LoggingService,
    _ngZone: NgZone,
    private sf: StringFormatterService,
  ) {
    super(
      _analyticsService,
      _authService,
      _exportService,
      _toastrService,
      _loggingService,
      UserSessionsConfig,
      _ngZone,
    );

    this.handleDataSubscription(this.analyticsService.userSessionsDataChange);

    this.analyticsService.userSessionsDataChange.subscribe((data) => {
      this.setData(data);
    });

    this.analyticsService.userAnalyticsConfigurationChartDisplayOptionsChange.subscribe(
      () => {
        this.chartData.labels = this.userSessionData.labels;
        this.chartData.datasets = this.userSessionData.datasets;
        if (this.analyticsData?.labels) {
          this.analyticsData.labels = this.userSessionData.labels;
        }
        if (this.userSessionData.datasets.length > 0) {
          this.analyticsData.data = this.userSessionData.datasets[0].data;
        }
        this.config.generalSettings.labels = this.userSessionData.labels;

        // Re-apply color scheme to all datasets when display options change
        this.applyColorSchemeToDatasets();
      },
    );
  }

  dataPointKey: string = "Total"; //I am here
  protected getDataPointDisplayValue(dataPoint: UserLineChartDataPoint): any {
    return dataPoint.Total;
  }

  buildFilterSettings(): void {
    this.loggingService.log("UserSessionsChartComponent - buildFilterSettings");
    this.filterSettings.FilterByAllEmployees = "true";
    this.addPeriodtoFilterSettings();
    this.addPermissionsToFilterSettings();
    this.addExpirationPeriodtoFilterSettings();
  }

  protected processDataForChart(data?: UserSessionsData) {
    // Call the parent method if needed
    this.setData(data);

    super.processDataForChart(data);

    this.loggingService.log(
      "Total Users Chart - processDataForChart - custom logic applied",
    );
  }

  refreshData(): void {
    this.isLoading = true;
    this.filterSettings.FilterByAllEmployees = "true";
    this.addPeriodtoFilterSettings();
    this.addPermissionsToFilterSettings();
    this.addExpirationPeriodtoFilterSettings();
    this.loggingService.log("Getting User Sessions Data");
    this.analyticsService.getUserSessionsData(this.filterSettings);
  }

  ngOnInit(): void {
    super.init();
  }

  ngOnDestroy(): void {
    super.cleanup();
  }

  // Permission type labels
  private permissionLabels: { [key: number]: string } = {
    1: "Application Administrator",
    2: "Komatsu Employee",
    3: "Parent Company",
    4: "Minesite",
    5: "Modified Distributor/Customer",
  };

  // Get colors from the configured color scheme
  private getColorSchemeColors(): string[] {
    const colorScheme = this.config?.generalSettings?.colorScheme;
    if (colorScheme) {
      const [group, scheme] = colorScheme.split(".");
      if (group in ColorSchemes && scheme in ColorSchemes[group]) {
        return ColorSchemes[group][scheme];
      }
    }
    // Fall back to default color scheme
    return ColorSchemes.other.default;
  }

  // Apply current color scheme to all datasets
  private applyColorSchemeToDatasets(): void {
    const colors = this.getColorSchemeColors();
    
    // Update colors on chartData datasets
    this.chartData.datasets.forEach((dataset, index) => {
      const color = colors[index % colors.length];
      dataset.borderColor = color;
      dataset.backgroundColor = color;
    });

    // Also update userSessionData to keep in sync
    this.userSessionData.datasets.forEach((dataset, index) => {
      const color = colors[index % colors.length];
      dataset.borderColor = color;
      dataset.backgroundColor = color;
    });
  }

  private setData(data: UserSessionsData): void {
    if (data && data.DataPoints.length > 0) {
      this.isLoading = false;
      this.loggingService.log("User Sessions Data Changed");
      this.loggingService.log(data);

      // Sort data points by date
      let dataPointsList = data.DataPoints.sort((a, b) => {
        let aDate = new Date(a.RangeStart);
        let bDate = new Date(b.RangeStart);
        return aDate.getTime() - bDate.getTime();
      });

      // Check if we should display separate lines for each user type
      const isSeparateMode =
        this.config?.displayMode === "separate" &&
        this.config?.userPermission == UserPermissions.All;

      if (isSeparateMode) {
        this.setDataSeparate(dataPointsList);
      } else {
        this.setDataCombined(dataPointsList);
      }
    }
    this.hasData = true;
  }

  private setDataCombined(dataPointsList: UserLineChartDataPoint[]): void {
    const aggregatedData: { date: string; total: number }[] = [];
    let currentDate = "";
    let currentTotal = 0;

    for (const dataPoint of dataPointsList) {
      let date = new Date(dataPoint.RangeStart);
      const formattedDate = this.sf.formatDateTime(date);
      if (formattedDate !== currentDate) {
        if (currentDate) {
          // Add the previous date's points to the aggregated data
          aggregatedData.push({ date: currentDate, total: currentTotal });
        }
        // Reset for the new date
        currentDate = formattedDate;
        currentTotal = dataPoint.Total;
      } else {
        // Accumulate points for the same date
        currentTotal += dataPoint.Total;
      }
    }

    // Add the last date's points to the aggregated data
    if (currentDate) {
      aggregatedData.push({ date: currentDate, total: currentTotal });
    }

    this.userSessionData.labels = aggregatedData.map((item) => item.date);
    this.userSessionData.datasets = [
      {
        data: aggregatedData.map((item) => item.total),
        label: "User Sessions",
        borderColor: "#3cba9f",
      },
    ];
    this.chartData.labels = aggregatedData.map((item) => item.date);
    this.chartData.datasets = [
      {
        data: aggregatedData.map((item) => item.total),
        label: "User Sessions",
        datalabels: {
          labels: this.labelSet,
        },
      },
    ];
    this.config.generalSettings.labels = aggregatedData.map(
      (item) => item.date,
    );
  }

  private setDataSeparate(dataPointsList: UserLineChartDataPoint[]): void {
    // Group data points by date and permission
    const groupedByDateAndPermission: {
      [date: string]: { [permissionId: number]: number };
    } = {};
    const allDates: string[] = [];
    const allPermissionIds = new Set<number>();

    for (const dataPoint of dataPointsList) {
      let date = new Date(dataPoint.RangeStart);
      const formattedDate = this.sf.formatDateTime(date);

      if (!groupedByDateAndPermission[formattedDate]) {
        groupedByDateAndPermission[formattedDate] = {};
        allDates.push(formattedDate);
      }

      const permId = dataPoint.PermissionId || 0;
      if (permId > 0) {
        allPermissionIds.add(permId);
        if (!groupedByDateAndPermission[formattedDate][permId]) {
          groupedByDateAndPermission[formattedDate][permId] = 0;
        }
        groupedByDateAndPermission[formattedDate][permId] += dataPoint.Total;
      }
    }

    // Sort dates
    const sortedDates = [...new Set(allDates)];

    // Create datasets for each permission type
    const datasets: any[] = [];
    const sortedPermissionIds = Array.from(allPermissionIds).sort(
      (a, b) => a - b,
    );

    // Get colors from the configured color scheme
    const colors = this.getColorSchemeColors();

    for (let i = 0; i < sortedPermissionIds.length; i++) {
      const permId = sortedPermissionIds[i];
      const dataForPermission = sortedDates.map(
        (date) => groupedByDateAndPermission[date][permId] || 0,
      );

      // Use color from scheme, cycling if more datasets than colors
      const color = colors[i % colors.length];

      datasets.push({
        data: dataForPermission,
        label: this.permissionLabels[permId] || `Permission ${permId}`,
        borderColor: color,
        backgroundColor: color,
        fill: false,
        datalabels: {
          labels: this.labelSet,
        },
      });
    }

    this.userSessionData.labels = sortedDates;
    this.userSessionData.datasets = datasets;
    this.chartData.labels = sortedDates;
    this.chartData.datasets = datasets;
    this.config.generalSettings.labels = sortedDates;
  }
}
